/**
 * Client-side mirror of the backend lease engine.
 *
 * Keeps the formula in sync with /app/backend/lease_engine.py so the UI
 * can render live "vanaf €X / maand" values without a round-trip, while the
 * server remains the source of truth for quotes, PDFs and admin.
 *
 *   monthly = (investment + max(investment * fee_percentage, fee_minimum))
 *             * lease_factor_monthly + service_fee_monthly
 */
import { api } from "@/lib/api";

export const DEFAULT_LEASE_SETTINGS = {
  lease_factor_monthly: 0.0219,
  fee_percentage: 0.10,
  fee_minimum: 500,
  service_fee_monthly: 0,
  default_contract_duration: 60,
  rounding: 2,
};

const round = (v, decimals = 2) => {
  const mult = Math.pow(10, decimals);
  return Math.round(Number(v) * mult) / mult;
};

/** Pure computation, mirrors backend exactly. */
export function computeMonthly(investment, settings = DEFAULT_LEASE_SETTINGS) {
  const s = { ...DEFAULT_LEASE_SETTINGS, ...(settings || {}) };
  const inv = Number(investment) || 0;
  if (inv <= 0) {
    return {
      investment_amount: 0,
      fee: 0,
      total_financed_amount: 0,
      monthly_lease: 0,
      service_fee_monthly: round(s.service_fee_monthly, s.rounding),
      monthly: 0,
      contract_duration: s.default_contract_duration,
    };
  }
  const calculated_fee = inv * s.fee_percentage;
  const fee = calculated_fee >= s.fee_minimum ? calculated_fee : s.fee_minimum;
  const total_financed = inv + fee;
  const monthly_lease = total_financed * s.lease_factor_monthly;
  const total_monthly = monthly_lease + s.service_fee_monthly;
  return {
    investment_amount: round(inv, s.rounding),
    fee: round(fee, s.rounding),
    total_financed_amount: round(total_financed, s.rounding),
    monthly_lease: round(monthly_lease, s.rounding),
    service_fee_monthly: round(s.service_fee_monthly, s.rounding),
    monthly: round(total_monthly, s.rounding),
    contract_duration: s.default_contract_duration,
  };
}

/** Fetch the live settings once per app session and cache in memory. */
let _cached = null;
export async function loadLeaseSettings() {
  if (_cached) return _cached;
  try {
    const { data } = await api.get("/lease/settings");
    _cached = { ...DEFAULT_LEASE_SETTINGS, ...data };
  } catch {
    _cached = { ...DEFAULT_LEASE_SETTINGS };
  }
  return _cached;
}
